import { create } from 'zustand';
import { IChat, IMessage, ObjectId, WarehouseType } from '../types';
import { clearDeletedMessages, sortMessagesByCreatedAt } from './dataUtils';
import { IImages } from '../interfaces/chat-image';

type DataStore = {
	//state..
	version: number;
	chatsFetched: boolean;
	chats: Array<Partial<IMessage>>;
	id: any;
	driver: string;
	warehouse: string;

	// read & write state
	chatsImages: Array<string>;
	chatMessageDoc: Partial<IMessage>;
	chatMessageImageDoc: Partial<IMessage>;
	selectedImages: Array<string>;
	imageDoc: Partial<IImages[]>;

	currentWarehouse: Partial<WarehouseType>;
	currentChatRoom: Partial<IChat>;
	setCurrentChatRoom: (room: Partial<IChat>) => void;
	setCurrentWarehouse: (room: Partial<WarehouseType>) => void;

	//mutations..
	updateChatsData: (value: Partial<IMessage>, id?: ObjectId) => void;
	updateChatsImages: (value: Array<string>) => void;
	updateChatMessageDoc: (value: Partial<IMessage>) => void;

	clearChatMessage: () => void;
	clearChats: () => void;

	setVersion: (value: number) => void;
	setChatId: (id: any) => void;
	setChatsFetched: (value: boolean) => void;
	setChatsData: (value: Array<Partial<IMessage>>) => void;
	setSelectedImages: (value: string) => void;
	resetSelectedImages: () => void;
	popSelectedImage: (index: number) => void;
	setImageDoc: (value: any) => void;
	setDriverAndWarehouse: (driver: string, warehouse: string) => void;
};

export const useDataStore = create<DataStore>((set, get) => ({
	version: 0,
	chats: [],
	id: '',
	chatsFetched: false,
	shipmentsFetched: false,
	driver: '',
	warehouse: '',

	chatsImages: [],
	chatMessageDoc: {},
	chatMessageImageDoc: {},
	selectedImages: [],
	imageDoc: [],

	currentChatRoom: null,
	currentWarehouse: null,

	setCurrentChatRoom(room) {
		set({ currentChatRoom: room });
	},
	setCurrentWarehouse(warehouse) {
		set({ currentWarehouse: warehouse });
	},

	setVersion: (value) => {
		set({ version: value });
	},
	setChatId: (id) => {
		set({ id: id });
	},
	setChatsFetched: (value) => set({ chatsFetched: value }),
	setChatsData: (value) => {
		set((state) => {
			const existingChats = state.chatsFetched ? state.chats : [];
			const newChats = [...existingChats, ...value];
			return { chats: newChats, chatsFetched: true };
		});
	},
	updateChatsData: (value, id) => {
		if (!id) {
			const chats = get().chats;
			const newChats = [...chats, value];
			set({ chats: sortMessagesByCreatedAt(newChats), chatsFetched: true });
			return;
		}
		const updatedChats = clearDeletedMessages(id, get().chats);
		set({ chats: sortMessagesByCreatedAt(updatedChats) });
	},
	updateChatsImages: (value) => {
		set({ chatsImages: value });
	},
	updateChatMessageDoc: (value) => set({ chatMessageDoc: value }),
	clearChatMessage: () => set({ chatMessageDoc: null, chatMessageImageDoc: null, chatsImages: [] }),
	clearChats: () => set({ chats: [] }),

	setDriverAndWarehouse: (driver, warehouse) => set({ driver, warehouse }),
	resetSelectedImages: () => set({ selectedImages: [] }),
	setSelectedImages: (value: string) =>
		set((state) => ({
			selectedImages: [...(state.selectedImages || []), value]
		})),
	popSelectedImage: (index: number) =>
		set((state) => {
			if (!state.selectedImages || index < 0 || index >= state.selectedImages.length) {
				return state;
			}
			const newSelectedImages = [...state.selectedImages];
			newSelectedImages.splice(index, 1);
			return { selectedImages: newSelectedImages };
		}),
	setImageDoc: (value) => {
		set({ imageDoc: value });
	}
}));
